/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoOutlined } from '@mui/icons-material';
import { Box, Button, Card, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { TouchRippleActions } from '@mui/material/ButtonBase/TouchRipple';

import { CIColors, ServiceType } from 'shared';

import FromBackupIcon from '../../assets/images/from_backup_icon.svg';
import { ServiceIcon } from '../../components/ServiceIcon';
import { TiltEffect } from '../../components/TiltEffect';
import { useSystemConfig } from '../../providers/SystemConfigProvider';
import { CreateClusterParams } from '../../store/createCluster/types';
import { EntityId } from '../../types/api/EntityId';
import { useControlOverlays } from '../../utils/useControlOverlays';

type Props = {
  serviceType: ServiceType;
  matchParams: {
    organizationId?: EntityId;
    projectId?: EntityId;
    clusterId?: EntityId;
  };
};

export const ServiceCard = memo((props: Props) => {
  const {
    serviceType,
    matchParams: { organizationId, projectId, clusterId },
  } = props;

  const { t } = useTranslation();

  const systemConfig = useSystemConfig();
  const isEnabled = systemConfig.services[props.serviceType];

  const { doOpenCreateClusterOverlay, doCloseCreateServiceOverlay } = useControlOverlays();

  const onClick = useCallback(
    (args: { fromBackup: boolean }) => {
      if (isEnabled) {
        const params: CreateClusterParams = {
          serviceType,
          organizationId,
          projectId,
          clusterId,
          fromBackup: args.fromBackup,
        };
        doCloseCreateServiceOverlay();
        doOpenCreateClusterOverlay(params);
      }
    },
    [
      clusterId,
      doCloseCreateServiceOverlay,
      doOpenCreateClusterOverlay,
      isEnabled,
      organizationId,
      projectId,
      serviceType,
    ],
  );

  // Used within the `From Backup` button, so that it stands out properly
  const touchRippleRef = useRef<TouchRippleActions>(null);

  return (
    <TiltEffect>
      <Tooltip
        arrow
        title={
          !isEnabled ? (
            <Stack>
              <Box sx={{ marginX: 'auto' }}>{t('general:services.disabled.hint')}</Box>
              <Box sx={{ marginX: 'auto' }}>{t('general:services.disabled.cta')}</Box>
            </Stack>
          ) : (
            ''
          )
        }
      >
        <Card
          elevation={24}
          sx={{
            width: '330px',
            borderRadius: '6px',
            transition: 'all .2s ease-in-out',
            '&:hover': {
              cursor: isEnabled ? 'pointer' : 'not-allowed',
              '.info-button': { opacity: 1 },
              '.service-icon-container': { backgroundSize: '100% 180%' },
            },

            ...(!isEnabled
              ? { filter: 'saturate(0.3) opacity(0.9)', backgroundColor: 'rgba(255, 255, 255, 0.5)' }
              : {}),
          }}
          onClick={() => onClick({ fromBackup: false })}
          data-cy={`create-service-${serviceType}`}
        >
          <Stack
            className="service-icon-container"
            height="300px"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'relative',
              backgroundColor: '#FFFFFF',

              backgroundImage: `radial-gradient(circle at center bottom, ${CIColors.Secondary} 30%, #fff 55%)`,
              // Glow effect is done by moving in the radial circle from the bottom (see :hover of card)
              backgroundSize: '100% 250%',
              backgroundRepeat: 'no-repeat',
              transition: '.4s',
            }}
          >
            <ServiceIcon serviceType={props.serviceType} width="250px" height="auto" />
            <Tooltip title={t('general:services.infoButtonTooltip')} arrow placement="top">
              <IconButton
                className="info-button"
                sx={{
                  transition: 'all .2s ease-in-out',
                  position: 'absolute',
                  right: '8px',
                  top: '8px',
                  opacity: 0,
                }}
                target="_blank"
                href={t(`general:services.types.${props.serviceType}.productLink`)}
                onClick={e => e.stopPropagation()}
              >
                <InfoOutlined />
              </IconButton>
            </Tooltip>
          </Stack>
          <Stack
            height="250px"
            sx={{ backgroundColor: CIColors.Primary, justifyContent: 'space-between', padding: '32px 16px' }}
            gap={1}
          >
            <Box>
              <Typography textAlign="center" variant="h2" sx={{ color: '#FFF' }}>
                {t(`general:services.types.${props.serviceType}.short`)}
              </Typography>
              <Typography textAlign="center" variant="subtitle1" sx={{ color: CIColors.Secondary }}>
                {t(`general:services.types.${props.serviceType}.flavor`)}
              </Typography>
            </Box>
            {props.serviceType === 'postgres' ? (
              <Button
                sx={{ color: CIColors.Secondary }}
                touchRippleRef={touchRippleRef}
                onMouseEnter={() => touchRippleRef.current?.pulsate()}
                onMouseLeave={() => touchRippleRef.current?.stop()}
                startIcon={<img src={FromBackupIcon} />}
                onClick={e => {
                  e.stopPropagation();
                  onClick({ fromBackup: true });
                }}
              >
                From backup
              </Button>
            ) : (
              <Box />
            )}
          </Stack>
        </Card>
      </Tooltip>
    </TiltEffect>
  );
});
